import { Checkbox, TextField, Typography, useTheme } from '@mui/material';
import { BudgetValueState, IBudget, IBudgetPeriod } from 'common/entities';
import { IMonth, ITableColumn } from 'common/types';
import SortableTable from 'components/Shared/SortableTable';
import EditableNumberCell from 'components/table/EditableNumberCell';
import { useAppSelector } from 'core/hooks/hooks';
import { getMonthPeriod } from 'core/services/budget.service';
import { sumWithCurrency } from 'core/services/currency.service';
import { setMonthBudget } from 'core/store/actions/budgetMonth.actions';
import { periodSelector } from 'core/store/selectors/period.selector';
import { useCallback, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { BudgetMonthContext } from './BudgetMonthContextProvider';
import BudgetMonthPanel from './BudgetMonthPanel';

export interface BudgetMonthAccumulationsPanelProps {
  budgets: IBudget[];
}

interface IMonthAccumulationRecord {
  id: string;
  name: string;
  target?: number;
  monthBudget?: number;
  selected: boolean;
}

const getAccumulationColumns = (
  isEditing: boolean
): ITableColumn<IMonthAccumulationRecord>[] => [
  ...((isEditing
    ? [
        {
          id: 'selected',
          numeric: false,
          label: '',
          editable: false
        }
      ]
    : []) as ITableColumn<IMonthAccumulationRecord>[]),
  {
    id: 'name',
    numeric: false,
    label: 'Accumulation'
  },
  {
    id: 'target',
    numeric: true,
    label: 'Target'
  },
  {
    id: 'monthBudget',
    numeric: true,
    label: 'Month budget'
  }
];

function createAccumulationRow(
  budget: IBudget,
  selected: boolean,
  value: number | undefined
): IMonthAccumulationRecord {
  return {
    id: budget.id,
    name: budget.name,
    target: budget.accumulationGoal,
    monthBudget: value,
    selected: selected
  };
}

const createRows = (
  budgets: IBudget[],
  selectedBudgets: Record<string, BudgetValueState>,
  isEditing: boolean
) => {
  const accumulationRows = budgets
    .filter(
      b =>
        b.accumulation &&
        ((isEditing && !b.closed) || selectedBudgets[b.id]?.selected)
    )
    .map(b =>
      createAccumulationRow(
        b,
        !!selectedBudgets[b.id]?.selected,
        selectedBudgets[b.id]?.value ?? b.defaultBudget ?? 0
      )
    );
  return accumulationRows;
};

const renderBudgetSumItem = (value: any) => <>{sumWithCurrency(value)}</>;

const BudgetMonthAccumulationsPanel = ({
  budgets
}: BudgetMonthAccumulationsPanelProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const budgetMonth = useAppSelector(periodSelector);
  const {
    isEditing,
    selectedBudgets,
    budgetValues,
    onUpdateBudgetValue,
    onBudgetToggleSelected
  } = useContext(BudgetMonthContext);

  const accumulationRows = useMemo(
    () => createRows(budgets, selectedBudgets, isEditing),
    [budgets, isEditing, selectedBudgets]
  );

  const accumulationsColumns = useMemo(
    () => getAccumulationColumns(isEditing),
    [isEditing]
  );

  const createBudgetMonth = useCallback(
    (budgetSum: number | undefined): IBudgetPeriod => ({
      month: budgetMonth.month,
      year: budgetMonth.year,
      budget: budgetSum
    }),
    [budgetMonth]
  );

  const handleUpdateBudgetMonthSum = useCallback(
    (budgetId: string, budgetMonth: IBudgetPeriod) => {
      dispatch(
        setMonthBudget({
          budgetId,
          budget: budgetMonth.budget,
          month: budgetMonth.month,
          year: budgetMonth.year
        })
      );
    },
    [dispatch]
  );

  const accumulationsCustomCellsBuilder = useCallback(
    (
      column: keyof IMonthAccumulationRecord,
      row: IMonthAccumulationRecord
    ): JSX.Element => {
      switch (column) {
        case 'selected':
          return (
            <Checkbox
              checked={!!budgetValues[row.id]?.selected}
              onChange={() => onBudgetToggleSelected(row.id)}
            />
          );
        case 'name':
          return row.id === '0' ? (
            <Typography
              component={'span'}
              sx={{ color: '#555', fontSize: '12px' }}
            >
              {row.name}
            </Typography>
          ) : (
            <Link
              to={{
                pathname: `/accumulation/${row.id}`,
                state: {
                  goBackTitle: 'Back to Month Budgets'
                }
              }}
              style={{ color: theme.palette.common.black }}
            >
              {row.name}
            </Link>
          );
        case 'monthBudget':
          return isEditing ? (
            <TextField
              sx={{ width: '80px' }}
              type="number"
              variant="standard"
              value={budgetValues[row.id]?.value ?? ''}
              onChange={e =>
                onUpdateBudgetValue(
                  row.id,
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              disabled={!budgetValues[row.id]?.selected}
            />
          ) : (
            <>
              <EditableNumberCell
                initialValue={Number(row.monthBudget) ?? 0}
                onSaveValue={(value: number) =>
                  handleUpdateBudgetMonthSum(row.id, createBudgetMonth(value))
                }
                renderValue={renderBudgetSumItem}
              />
            </>
          );
        case 'target':
          return <>{sumWithCurrency(row[column] ?? 0)}</>;
        default:
          return <>{row[column]}</>;
      }
    },
    [isEditing, budgetValues]
  );

  return (
    <BudgetMonthPanel title="Accumulations">
      <SortableTable
        columns={accumulationsColumns}
        rows={accumulationRows}
        indexBy="id"
        defaultOrderBy={'name'}
        customCellBuilder={accumulationsCustomCellsBuilder}
        styles={{
          cell: { borderBottom: 'none', px: 0, py: '.3rem' },
          headCell: { borderBottom: 'none', px: 0, py: '.3rem' }
        }}
      />
    </BudgetMonthPanel>
  );
};

export default BudgetMonthAccumulationsPanel;
